import { createContext, useEffect, useState } from 'react';
import {
  AirQualityForecast,
  AirQualityObservation,
  Day15Forecast,
  DetailedForecast,
  HourlyForecast,
  WeatherHome,
} from 'components';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import TabsHeader from 'components/TabsHeader/TabsHeader';
import { useSearchParams } from 'react-router-dom';
import MobileTabsHeader from 'components/TabsHeader/MobileTabsHeader';
import { capitalizeWord } from 'utils/stringUtils';
import { Container } from 'react-bootstrap';
import {
  currentCityConditions,
  get15DayForecast,
  getAstronomyData,
  getDetailedForecastData,
  getHourlyForecast,
} from 'services/weather/weather.service';
import {
  getAirQualityForecast,
  getAirQualityObservation,
} from 'services/weather/airQuality.service';

export const WeatherContext = createContext({
  data: {},
});

const daysOfWeekArray = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const topTabs = [
  {
    option: 'Weather Home',
  },
  {
    option: 'Hourly Forecast',
  },
  {
    option: 'Detailed Forecast',
  },
  {
    option: '15-Day Forecast',
  },
  {
    option: 'Air Quality',
    options: [
      {
        id: 'air-quality-observations',
        title: 'Observations',
      },
      {
        id: 'air-quality-forecast',
        title: 'Forecast',
      },
    ],
  },
];

const airQualitySubTabs = [
  { option: 'Air Quality Observations' },
  { option: 'Air Quality Forecast' },
];

export default function Forecast() {
  const [weatherData, setWeatherData] = useState({
    fetching: true,
  });
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('tab')?.toLowerCase());
  const [day, setDay] = useState(
    params.get('day')?.toLowerCase() || daysOfWeekArray[new Date().getDay()].toLowerCase(),
  );
  const [subtab, setSubtab] = useState(params.get('subtab')?.toLowerCase());

  useEffect(() => {
    //Get weather data
    (async () => {
      const promises = {
        hourlyForecast: getHourlyForecast(),
        currentConditions: currentCityConditions(),
        astronomy: getAstronomyData(),
        detailed: getDetailedForecastData(),
        day15: get15DayForecast(),
        airObservation: getAirQualityObservation(),
        airForecast: getAirQualityForecast(),
      };

      await Promise.allSettled(Object.values(promises)).then((results) => {
        results.forEach(
          (result, index) =>
            (promises[Object.keys(promises)[index]] =
              result.status === 'fulfilled' ? result.value : null),
        );

        //get air quality stations
        let airQualityStations = { observation: [], forecast: [] };
        promises?.airForecast?.data?.result?.forEach((item) => {
          if (!item.air_station_name) return;
          if (!airQualityStations.forecast.includes(item?.air_station_name))
            airQualityStations.forecast.push(item.air_station_name);
        });

        const newObject = {
          hourly: promises?.hourlyForecast?.data?.result || [],
          currentConditions: promises?.currentConditions?.data?.result || [],
          astronomy: promises?.astronomy?.data?.result || {},
          detailed: promises?.detailed?.data?.result || [],
          day15: promises?.day15?.data?.result || [],
          airQuality: {
            observation: promises?.airObservation?.data?.result || [],
            forecast: {
              stations: airQualityStations.forecast,
              data: promises?.airForecast?.data?.result || [],
            },
          },
          fecthing: false,
        };

        setWeatherData(newObject);
      });
    })();
  }, []);

  const renderAirQualityTab = () => {
    switch (subtab) {
      case 'air quality observations':
        return (
          <div className="d-flex flex-column w-100 h-100" id="air-quality-observations">
            <AirQualityObservation />
          </div>
        );
      case 'air quality forecast':
        return (
          <div className="d-flex flex-column w-100 h-100" id="air-quality-forecast">
            <AirQualityForecast />
          </div>
        );
      default:
        return (
          <div className="d-flex flex-column w-100 h-100" id="air-quality-observations">
            <AirQualityObservation />
          </div>
        );
    }
  };

  const renderTab = () => {
    switch (params.get('tab')?.toLowerCase()) {
      case 'weather home':
        return (
          <div className="d-flex flex-column w-100 h-100" id="home">
            <WeatherHome />
          </div>
        );
      case 'hourly forecast':
        return (
          <div className="d-flex flex-column w-100 h-100" id="hourly">
            <HourlyForecast />
          </div>
        );
      case 'detailed forecast':
        return (
          <div className="d-flex flex-column w-100 h-100" id="detailed">
            <DetailedForecast selectedDay={capitalizeWord(day)} />
          </div>
        );
      case '15-day forecast':
        return (
          <div className="d-flex flex-column w-100 h-100" id="15-day">
            <Day15Forecast />
          </div>
        );
      case 'air quality':
        return renderAirQualityTab();
      default:
        return (
          <div className="d-flex flex-column w-100 h-100" id="home">
            <WeatherHome />
          </div>
        );
    }
  };

  useEffect(() => {
    setTab(params.get('tab')?.toLowerCase());
    setDay(params.get('day')?.toLowerCase());
    setSubtab(params.get('subtab')?.toLowerCase());
  }, [params]);

  if (weatherData?.fetching) return <WidgetLoadingAnimation />;

  return (
    <WeatherContext.Provider value={weatherData}>
      <Container fluid className="d-flex flex-column w-100 h-100 user-select-none pb-3">
        <div className="d-none d-lg-block flex-shrink-0">
          <TabsHeader
            borderRadius={
              tab === 'detailed forecast' || tab === 'air quality' ? '8px 8px 0 0' : '8px'
            }
            options={topTabs}
          />
          {tab === 'detailed forecast' && (
            <TabsHeader
              param="day"
              borderRadius={'0 0 8px 8px'}
              options={daysOfWeekArray.map((day) => ({ option: day }))}
              variant="primary"
            />
          )}
          {tab === 'air quality' && (
            <TabsHeader
              param="subtab"
              borderRadius={'0 0 8px 8px'}
              options={airQualitySubTabs}
              variant="primary"
            />
          )}
        </div>
        <div className="d-block d-lg-none flex-shrink-0">
          <MobileTabsHeader options={topTabs} />
          {tab === 'detailed forecast' && (
            <MobileTabsHeader
              param="day"
              options={daysOfWeekArray.map((day) => ({ option: day }))}
            />
          )}
          {tab === 'air quality' && (
            <MobileTabsHeader
              param="subtab"
              borderRadius={'0 0 8px 8px'}
              options={airQualitySubTabs}
              variant="primary"
            />
          )}
        </div>
        <Container fluid className="py-3 flex-grow-1 overflow-auto min-h-0">
          {renderTab()}
        </Container>
      </Container>
    </WeatherContext.Provider>
  );
}
